import { Component, OnInit, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActiveCommentInterface } from 'src/app/types/active-comment.interface';
import { CommentInterface } from 'src/app/types/comment.interface';
import { CommentsService } from './comments.service';
import { UserService } from '../../../user.service';
import { SignInModalComponent } from '../sign-in-modal/sign-in-modal.component';

@Component({
  selector: 'app-comments',
  templateUrl: './comments.component.html',
  styleUrls: ['./comments.component.scss']
})
export class CommentsComponent implements OnInit {
  @Input() articleId!: string;
  @Input() commentType!: string;

  isLoggedIn = this.userService.isLoggedIn();
  comments: CommentInterface[] = [];
  activeComment: ActiveCommentInterface | null = null;
  commentGrid: number = 1;
  liked: boolean = false;
  displayedRootComments: CommentInterface[] = [];
  rootCommentsPerPage = 3;
  currentRootPage = 1;
  private preventPopup = false;
  newCommentText: string = ''; // This is the text of the new comment

  constructor(private dialog: MatDialog, private commentsService: CommentsService, private userService: UserService) {}

  ngOnInit(): void {
    this.commentsService.getComments(this.articleId).subscribe((comments) => {
      this.comments = comments.data;
      this.rootCommentsPerPage = 2;
      this.currentRootPage = 1;
      this.updateDisplayedRootComments();
      this.showComments = true; // important pour afficher par défaut
    });
  }

  getRootComments(): CommentInterface[] {
    return this.comments
      .filter((comment) => comment.attributes.parentId === null)
      .sort((a, b) => new Date(b.attributes.createdAt).getTime() - new Date(a.attributes.createdAt).getTime());
  }

  updateDisplayedRootComments() {
    const start = 0;
    const end = this.currentRootPage * this.rootCommentsPerPage;
    this.displayedRootComments = this.getRootComments().slice(start, end);
  }


  loadMoreRootComments() {
    this.currentRootPage++;
    this.updateDisplayedRootComments();
  }
    

  updateComment({
    text,
    commentId,
  }: {
    text: string;
    commentId: string;
  }): void {
    this.commentsService.updateComment(commentId, text).subscribe((updatedComment) => {
      // 1. Remplacer le commentaire dans le tableau principal
      this.comments = this.comments.map((comment) => {
        if (comment.id == commentId) {
          updatedComment.attributes.parentId = updatedComment.attributes.parentId || null;
          return updatedComment;
        }
        return comment;
      });
  
      // 2. Rafraîchir la vue affichée
      this.updateDisplayedRootComments(); // Si pagination des root comments
  
      // 3. Si c'est une réponse, tu veux peut-être re-trier les replies
      const parentComment = this.comments.find(c => c.id === updatedComment.attributes.parentId);
      if (parentComment) {
        // Re-trie les réponses du parent localement (par exemple par date)
        const replies = this.comments.filter(
          c => c.attributes.parentId === updatedComment.attributes.parentId
        ).sort((a, b) =>
          new Date(b.attributes.publishedAt).getTime() - new Date(a.attributes.publishedAt).getTime()
        );
  
        // Tu peux propager cette mise à jour en utilisant un EventEmitter si besoin
      }
  
      // 4. Fermer le champ d’édition
      this.activeComment = null;
    });
  }
  

  deleteComment(commentId: string): void {
    // Récupère tous les commentaires enfants récursivement
    const commentsToDelete = this.getAllChildrenComments(commentId);
    commentsToDelete.push(commentId); // inclut le commentaire parent
  
    // Supprime chaque commentaire un par un (en commençant par les enfants)
    commentsToDelete.reverse().forEach(id => {
      this.commentsService.deleteComment(id).subscribe(() => {
        this.comments = this.comments.filter(comment => comment.id !== id);
        this.updateDisplayedRootComments(); // met à jour l’affichage
      });
    });
  
    // Optionnel : réinitialiser l’état actif
    this.activeComment = null;
  }

  getAllChildrenComments(parentId: string): string[] {
    let result: string[] = [];
  
    const directChildren = this.comments.filter(c => c.attributes.parentId === parentId);
    for (let child of directChildren) {
      result.push(child.id);
      result = result.concat(this.getAllChildrenComments(child.id)); // récursif
    }
  
    return result;
  }
  
  

  setActiveComment(activeComment: ActiveCommentInterface | null): void {
    this.activeComment = activeComment;
  }

  addComment({
    text,
    parentId,
  }: {
    text: string;
    parentId: string | null;
  }): void {
    if (this.isLoggedIn) {
      this.commentsService
        .createComment(text, this.articleId, parentId)
        .subscribe((createdComment) => {
          this.comments = [createdComment, ...this.comments];
  
          // Update view if it's a root comment
          if (!parentId) {
            const visibleLimit = this.currentRootPage * this.rootCommentsPerPage;
            const rootCommentsCount = this.getRootComments().length;
            if (rootCommentsCount < visibleLimit + 1) {
              this.currentRootPage++;
            }
            this.updateDisplayedRootComments();
          } 
  
          this.activeComment = null;
          this.newCommentText = '';
        });
    }
  }
   

  
  // This is the text of the new comment
  submitComment(): void {
    const text = this.newCommentText.trim();
    if (!text) return;

    this.addComment({ text, parentId: null });
    this.newCommentText = '';
  }


  showComments = false;
  // This is the text of the new comment
  handleInputFocus() {
    if (!this.isLoggedIn && !this.preventPopup) {
      this.preventPopup = true;
  
      const dialogRef = this.dialog.open(SignInModalComponent, {
        width: '400px',
        disableClose: true
      });
  
      dialogRef.afterClosed().subscribe(() => {
        this.preventPopup = false;
  
        // retire le focus de l'input pour éviter un 2e déclenchement
        (document.activeElement as HTMLElement)?.blur();
      });
    }
  }  

  openSignInDialog() {
    // Supprime le focus pour éviter l'erreur liée à aria-hidden
    (document.activeElement as HTMLElement)?.blur();
  
    this.dialog.open(SignInModalComponent, {
      width: '400px',
      disableClose: true, // évite les fermetures involontaires
      autoFocus: false // évite le focus direct
    });    
  }  

  toggleComments(): void {
    this.showComments = !this.showComments;
  }

  submitRootComment(): void {
    const text = this.newCommentText.trim();
    if (!text) return;
  
    this.addComment({ text: this.newCommentText, parentId: null });
    this.newCommentText = '';
  }  


  getReplies(commentId: string): CommentInterface[] {
  
    return this.comments
      .filter((comment) => comment.attributes.parentId == commentId)
      .sort(
        (a, b) =>
          new Date(a.attributes.createdAt).getTime() - new Date(b.attributes.createdAt).getTime()
      );
  }

  setLikeOld(commentId: string): void {
    
    let commentToUpdate = this.comments.find(comment => comment.id === commentId);
  
    if (commentToUpdate !== undefined) {
      let numberOfLikesFromDB = commentToUpdate.attributes.likes  || 0;
      let nbLikes = this.liked ? numberOfLikesFromDB - 1 : numberOfLikesFromDB + 1;
      this.liked = !this.liked;
      this.commentsService.addLike(commentId, nbLikes).subscribe((comment) => {
        if (commentToUpdate !== undefined) {
         
          this.comments[this.comments.indexOf(commentToUpdate)] = comment;
          
        }
      })
    }
  }

  onToggleLike({ commentId, like }: { commentId: string; like: boolean }): void {
    const userId = this.userService.getUserId();
  
    const comment = this.comments.find(c => c.id === commentId);
    const likedByUsers: number[] =
      comment?.attributes.liked_by?.data?.map((u: any) => u.id) || [];
  
    this.commentsService
      .toggleLikeComment(commentId, userId, likedByUsers)
      .subscribe((updatedComment) => {
        this.comments = this.comments.map((comment) =>
          comment.id === updatedComment.id ? updatedComment : comment
        );
      });
  }
  
  
}
